import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { Alert, Button, Container, Content, FlexboxGrid } from 'rsuite';
import { LoadingSpiner } from '../../../../../components';
import { useGetCampaign } from '../../hooks/useGetCampaign';
import { useGetCampaignSetup } from '../../hooks/useGetCampaignSetup';
import { useGetLojasParticipantes } from '../../hooks/useGetLojasParticipantes';
import { useOffers } from '../../hooks/useOffer';
import { useCampanhaStore } from '../../store/campanha-store';
import Header from '../Components/Header';
import Steps from '../Components/Steps';
import { OfertasFilters } from '../OfertasFilters';
import { OfertasModalsWrapper } from './Components/OfertasModalsWrapper';
import { OfferTable } from './Components/OfferTable/OfferTable';
import { useOfferInlineEditStore } from './Components/OfferTable/hooks/useOfferInlineEditStore';
import { RenderCountMessage } from './Components/RenderCountMessage';
import type { ProdutoDetalhado } from './hooks/useProdutosCampanha';
import { programarOferta } from './services';
import type { Campaign, IRowData, OfferBlockOption, OfferFormState, Store } from './types/offer.types';
import { useOfferTable } from './useOfferTable';
import { validateOffer } from './utils/offerValidation';
import { buildInitialProductFromOfferRow } from './utils/buildInitialProductFromOfferRow';
import { applyInlineOfferStateToRow } from './utils/applyInlineOfferStateToRow';

interface OfertasProps {
  history: any;
}

const buildInitialOffer = (rowData: IRowData): Record<string, any> | undefined => {
  const offer = rowData.offer_dto?.offer;
  if (!offer) return undefined;
  switch (offer.offer_type) {
    case 'SIMPLE_PRICE':
      return { ...offer, offer_price: rowData.offer_final_price };
    case 'FROM_TO':
      return { ...offer, to: rowData.offer_final_price };
    case 'BULK_PRICE':
      return { ...offer, price_per_unit: rowData.offer_final_price };
    case 'SIMPLE_PERCENTAGE':
    case 'BUY_GET_DISCOUNT':
    case 'BULK_PERCENTAGE':
    case 'UNIT_DISCOUNT':
      return { ...offer, percentage_discount: rowData.offer_discount_percentage };
    default:
      return offer;
  }
};

const Ofertas = ({ history }: OfertasProps) => {
  const { id } = useParams<{ id: string }>();

  const {
    states: { offerBlocks },
    handlers: { createOffer, editOffer, removeOffer },
  } = useOffers(id, null, { fetchOffers: false });

  const { data: campanha = {} as Campaign } = useGetCampaign(id);
  const { data: campaignSetup } = useGetCampaignSetup();
  const { data: lojasParticipantes = { content: [] } } = useGetLojasParticipantes(id, { size: 1000 });

  const { states: tableStates, handlers: tableHandlers } = useOfferTable();
  const { offers = { content: [] } } = tableStates;
  const { refetch: getSavedOffers } = tableHandlers;

  const blockOptions = useMemo(
    () =>
      offerBlocks?.content?.map((item: { name: string; id?: any }) => ({
        label: item.name,
        value: String(item.id),
      })) || [],
    [offerBlocks?.content],
  );

  const enrichedTableStates = useMemo(() => {
    if (!tableStates.offers?.content || !offerBlocks?.content?.length) return tableStates;
    return {
      ...tableStates,
      offers: {
        ...tableStates.offers,
        content: tableStates.offers.content.map((row: IRowData) => ({
          ...row,
          offer_group_id: row.offer_group_id ?? offerBlocks.content.find((b: any) => b.name === row.offer_group)?.id?.toString() ?? null,
        })),
      },
    };
  }, [tableStates, offerBlocks]);

  const offerBlockOptions: OfferBlockOption[] = useMemo(
    () =>
      offerBlocks?.content?.map((item: { name: string; id?: any }) => ({
        label: item.name,
        value: item.id,
      })) || [],
    [offerBlocks?.content],
  );

  const { selectedIds, selectedAll, setSelectedAll, setSelectedIds, resetOfertasFilters } = useCampanhaStore(id);

  useEffect(() => {
    return () => resetOfertasFilters();
  }, [id, resetOfertasFilters]);

  const hasSelectedIds = selectedIds.length > 0 || selectedAll;
  const inlineRows = useOfferInlineEditStore(state => state.rows);
  const isCampaignClosed = campanha.status === 'CLOSED';
  const canEditCurrentCampaignOffers = campanha.status === 'CURRENT' && campaignSetup?.editOffer === true;
  const isOfferReadOnly = isCampaignClosed || (campanha.status === 'CURRENT' && !canEditCurrentCampaignOffers);

  const [showModalCadastroOfertas, setShowModalCadastroOfertas] = useState<boolean>(false);
  const [oferta, setOferta] = useState<OfferFormState>({
    products: [],
    offer_groups: {},
    offer: {},
    offer_type: '',
  });
  const [showEditOferta, setShowEditOferta] = useState<boolean>(false);
  const [ofertaSelecionada, setOfertaSelecionada] = useState<string | null>(null);
  const [initialProducts, setInitialProducts] = useState<ProdutoDetalhado[] | undefined>(undefined);
  const [initialOffer, setInitialOffer] = useState<Record<string, any> | undefined>(undefined);
  const [initialOfferGroup, setInitialOfferGroup] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  const [showModalConfirmacaoExclusao, setShowModalConfirmacaoExclusao] = useState<boolean>(false);
  const [showModalProgramarCampanha, setShowModalProgramarCampanha] = useState<boolean>(false);
  const [ofertaToDelete, setOfertaToDelete] = useState<string | null>(null);
  const [showModalBulkEdit, setShowModalBulkEdit] = useState<boolean>(false);
  const [selectedOffersData, setSelectedOffersData] = useState<IRowData[]>([]);

  const currentOffers = useMemo(
    () => (offers.content ?? []).map((offer: IRowData) => applyInlineOfferStateToRow(offer, inlineRows[offer.product_uuid])),
    [offers.content, inlineRows],
  );

  const goToLojas = () => {
    history.push(`/ipa/campanhas/cadastro/lojas-participantes/${id}`);
  };

  const back = () => {
    history.push(`/ipa/campanhas/cadastro/dados-campanha/${id}`);
  };

  const draft = () => {
    history.push('/ipa/campanhas');
  };

  useEffect(() => {
    if (showModalCadastroOfertas) return;

    const timer = setTimeout(() => {
      setOfertaSelecionada(null);
      setInitialProducts(undefined);
      setInitialOffer(undefined);
      setInitialOfferGroup(null);
    }, 300);

    return () => clearTimeout(timer);
  }, [showModalCadastroOfertas]);

  const closeOferta = useCallback(() => {
    setShowModalCadastroOfertas(false);
    setShowEditOferta(false);
  }, []);

  const confirmOferta = useCallback(async () => {
    if (isOfferReadOnly) return;

    const ofertaData: OfferFormState = {
      ...oferta,
      products: oferta.products?.map((p: any) => ({
        ...p,
        ...(p.product_uuid && { id: p.product_uuid }),
      })),
    };
    const offerTypeSelected = ofertaData?.offer?.offer_type || '';
    const form = ofertaData?.offer || {};

    const validation = validateOffer(offerTypeSelected, form);

    if (!validation.isValid) {
      if (validation.errorMessage) {
        Alert.error(validation.errorMessage, 5000);
      }
      return;
    }

    try {
      const resp = showEditOferta ? await editOffer({ data: ofertaData, offerId: ofertaSelecionada! }) : await createOffer(ofertaData);

      if (resp?.status >= 200 && resp?.status < 300) {
        if (showEditOferta) {
          const productUuids = (ofertaData.products ?? []).map((p: any) => p.product_uuid).filter(Boolean);
          if (productUuids.length > 0) {
            useOfferInlineEditStore.getState().removeRows(productUuids);
          }
        }
        closeOferta();
        return resp;
      }
      Alert.error('Erro ao salvar a oferta.', 5000);
    } catch (err) {
      console.error(err);
      Alert.error('Erro ao salvar a oferta.', 5000);
    }
  }, [isOfferReadOnly, closeOferta, editOffer, createOffer, oferta, ofertaSelecionada, showEditOferta]);

  const idsLojasParticipantes: string[] = useMemo(
    () => lojasParticipantes?.content?.map((item: Store) => item.store_id_external) || [],
    [lojasParticipantes?.content],
  );

  const openEditOferta = useCallback(
    (oferta: IRowData) => {
      if (isOfferReadOnly) return;

      setShowEditOferta(true);
      setOfertaSelecionada(oferta.offer_dto?.id || null);
      setInitialProducts([buildInitialProductFromOfferRow(oferta) as ProdutoDetalhado]);
      setInitialOffer(buildInitialOffer(oferta));
      setInitialOfferGroup(oferta.offer_group || null);
      setShowModalCadastroOfertas(true);
    },
    [isOfferReadOnly],
  );

  const openBulkEditOferta = useCallback(() => {
    if (isOfferReadOnly) return;

    const data: IRowData[] = selectedAll ? currentOffers : currentOffers.filter((o: IRowData) => selectedIds.includes(o.product_uuid));
    setSelectedOffersData(data);
    setShowModalBulkEdit(true);
  }, [isOfferReadOnly, selectedAll, currentOffers, selectedIds]);

  const closeBulkEditOferta = useCallback(() => {
    setShowModalBulkEdit(false);
    setSelectedOffersData([]);
  }, []);

  const confirmBulkEditOferta = useCallback(() => {
    if (isOfferReadOnly) return;

    getSavedOffers();
    closeBulkEditOferta();
    setSelectedAll(false);
    setSelectedIds([]);
  }, [isOfferReadOnly, getSavedOffers, closeBulkEditOferta, setSelectedAll, setSelectedIds]);

  const clearSelection = useCallback(() => {
    setSelectedAll(false);
    setSelectedIds([]);
  }, [setSelectedAll, setSelectedIds]);

  const removeOferta = useCallback(
    (id: string) => {
      if (isOfferReadOnly) return;

      if (campanha.status === 'CURRENT') {
        setDeleteMessage('Esta oferta está em uma campanha ativa. A exclusão deste produto resultará na remoção automática da promoção.');
      } else {
        setDeleteMessage('Você está tentando deletar uma oferta.');
      }

      setShowModalConfirmacaoExclusao(true);
      setOfertaToDelete(id);
    },
    [campanha.status, isOfferReadOnly],
  );

  const closeExclusao = useCallback(() => {
    setShowModalConfirmacaoExclusao(false);
    setOfertaToDelete(null);
  }, []);

  const confirmExclusao = useCallback(async () => {
    if (isOfferReadOnly || !ofertaToDelete) return;

    try {
      await removeOffer(ofertaToDelete);
      Alert.success('Oferta excluída com sucesso.', 5000);
      closeExclusao();
    } catch {
      Alert.error('Erro ao excluir a oferta.', 5000);
    }
  }, [isOfferReadOnly, ofertaToDelete, removeOffer, closeExclusao]);

  const next = useCallback(async () => {
    if (campanha.status === 'CURRENT' || campanha.status === 'CLOSED') {
      history.push(`/ipa/campanhas/cadastro/extracao-precos/${id}`);
    } else {
      const resp = await programarOferta(id, 'SCHEDULED');
      if (resp) {
        history.push(`/ipa/campanhas/cadastro/extracao-precos/${id}`);
      }
    }
  }, [campanha.status, id, history]);

  const campaignTag = `#${campanha.campaign_type_order} ${campanha.campaign_type}`;
  const offersFound = offers?.totalElements || 0;

  const selectedCount = selectedAll ? offersFound : selectedIds?.length || 0;

  return (
    <Container className="main-container cadastro-ofertas-page">
      <Steps rota={history.location.pathname} campanhaId={id} status={campanha?.status} lojas={lojasParticipantes?.content?.length} />
      <LoadingSpiner size="md" />
      <Header
        history={history}
        id={id}
        name={campanha?.name}
        status={campanha?.status}
        backToCampaign={back}
        startingDate={campanha?.starting_date}
        expirationDate={campanha?.expiration_date}
        blocosOfertas={offerBlocks?.totalElements}
        lojasParticipantes={lojasParticipantes?.totalElements}
        ofertas={offers?.totalElements}
        campaignTag={campaignTag}
      />

      <Content className="filtros">
        <OfertasFilters />
      </Content>

      <Content className="contagem-ofertas">
        <FlexboxGrid justify="space-between" align="middle">
          <FlexboxGrid.Item colspan={12}>
            {hasSelectedIds ? (
              <div className="selected-count">
                <RenderCountMessage
                  count={selectedCount}
                  singularPrefix=""
                  pluralPrefix=""
                  zeroMessage="Nenhuma oferta selecionada"
                  singularSuffix=" selecionada"
                  pluralSuffix=" selecionadas"
                />
                <Button appearance="link" onClick={clearSelection}>
                  Limpar seleção
                </Button>
              </div>
            ) : (
              <div className="selected-count">
                <RenderCountMessage
                  count={offersFound}
                  singularPrefix="Foi encontrada"
                  pluralPrefix="Foram encontradas"
                  zeroMessage="Nenhuma oferta encontrada"
                  suffix=" no total"
                />
              </div>
            )}
          </FlexboxGrid.Item>
          <FlexboxGrid align="middle" className="grid-buttons">
            {/* Download ofertas vai entrar no futuro */}
            {/* <FlexboxGrid.Item>
              <Button appearance="default" className="download-button">
                <MdDownload />
              </Button>
            </FlexboxGrid.Item> */}
            {selectedIds.length > 1 || selectedAll ? (
              <FlexboxGrid.Item>
                <Button appearance="primary" onClick={openBulkEditOferta} disabled={isOfferReadOnly}>
                  EDITAR OFERTAS
                </Button>
              </FlexboxGrid.Item>
            ) : (
              <FlexboxGrid.Item>
                <Button
                  title="Criar bloco de oferta"
                  block
                  appearance="primary"
                  onClick={() => {
                    if (!isOfferReadOnly) setShowModalCadastroOfertas(true);
                  }}
                  disabled={isOfferReadOnly}
                >
                  ADICIONAR OFERTA
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </FlexboxGrid>
      </Content>

      {/* Tabela de ofertas */}
      <OfferTable
        states={enrichedTableStates}
        handlers={tableHandlers}
        onEdit={openEditOferta}
        onDelete={removeOferta}
        blockOptions={blockOptions}
        campaignId={id}
        storeIds={idsLojasParticipantes}
        isReadOnly={isOfferReadOnly}
      />

      <Content className="navegacao-ofertas">
        <FlexboxGrid justify="space-between" colspan={24}>
          <FlexboxGrid.Item>
            <Button appearance="link" onClick={goToLojas}>
              <ArrowBackIcon /> Voltar para lojas participantes
            </Button>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item>
            <FlexboxGrid>
              {campanha.status === 'DRAFT' ? (
                <FlexboxGrid.Item style={{ marginRight: '16px' }}>
                  <Button appearance="ghost" onClick={draft}>
                    SALVAR RASCUNHO
                  </Button>
                </FlexboxGrid.Item>
              ) : null}
              <FlexboxGrid.Item>
                <Button
                  appearance="primary"
                  disabled={!offers?.content?.length}
                  onClick={() => (campanha?.status === 'DRAFT' ? setShowModalProgramarCampanha(!showModalProgramarCampanha) : next())}
                  id="program-campaign-btn"
                >
                  <ArrowForwardIcon />{' '}
                  {campanha.status === 'CURRENT' || campanha.status === 'CLOSED'
                    ? 'EXTRAÇÃO DE PREÇOS'
                    : campanha.status === 'SCHEDULED'
                      ? 'PROGRAMAR ALTERAÇÕES'
                      : 'PROGRAMAR CAMPANHA'}
                </Button>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Content>

      {/* Modais centralizados */}
      <OfertasModalsWrapper
        showRegisterModal={showModalCadastroOfertas}
        onCloseRegister={closeOferta}
        onConfirmRegister={confirmOferta}
        campaignId={id}
        storeIds={idsLojasParticipantes}
        offerBlocks={offerBlockOptions}
        isEditMode={showEditOferta}
        selectedOfferId={ofertaSelecionada}
        initialProducts={initialProducts}
        initialOffer={initialOffer}
        initialOfferGroup={initialOfferGroup}
        onSetOferta={setOferta}
        showBulkEditModal={showModalBulkEdit}
        onCloseBulkEdit={closeBulkEditOferta}
        onConfirmBulkEdit={confirmBulkEditOferta}
        selectedOffers={selectedOffersData}
        showDeleteModal={showModalConfirmacaoExclusao}
        onConfirmDelete={confirmExclusao}
        onCancelDelete={closeExclusao}
        deleteMessage={deleteMessage}
        showScheduleModal={showModalProgramarCampanha}
        onCloseSchedule={() => setShowModalProgramarCampanha(current => !current)}
        onConfirmSchedule={next}
        campaign={campanha}
      />
    </Container>
  );
};

export default withRouter(Ofertas);
